import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import CabDefinitionForm from './CabDefinitionForm';
import ScheduleEntriesTab from './ScheduleEntriesTab';
import CabMeetingsTab from './CabMeetingsTab';
import {
  formDataToUpdatePayload,
  getCabDefinitionById,
  recordToFormData,
  updateCabDefinition,
} from '../../../services/cabDefinitionsService';
import type { CabDefinitionFormData } from '../../../types/cabDefinition';
import {
  hasValidationErrors,
  validateCabDefinitionForm,
  type CabDefinitionFormErrors,
} from '../../../utils/cabDefinitionValidation';

type DetailTab = 'schedule' | 'meetings';

const CabDefinitionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: DetailTab = tabParam === 'meetings' ? 'meetings' : 'schedule';

  const [formData, setFormData] = useState<CabDefinitionFormData | null>(null);
  const [errors, setErrors] = useState<CabDefinitionFormErrors>({});

  const { data: record, isLoading } = useQuery({
    queryKey: ['cab-definition', id],
    queryFn: () => getCabDefinitionById(id!),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (record) {
      setFormData(recordToFormData(record));
    }
  }, [record]);

  const updateMutation = useMutation({
    mutationFn: (payload: ReturnType<typeof formDataToUpdatePayload>) =>
      updateCabDefinition(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cab-definition', id] });
      queryClient.invalidateQueries({ queryKey: ['cab-definitions'] });
      // Future meetings inherit board/delegates/manager and refreshed attendees
      queryClient.invalidateQueries({ queryKey: ['cab-meetings', id] });
      queryClient.invalidateQueries({ queryKey: ['cab-meeting'] });
      queryClient.invalidateQueries({ queryKey: ['cab-meeting-attendees'] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meetings'] });
      queryClient.invalidateQueries({ queryKey: ['cab-workbench-meeting'] });
    },
  });

  const handleChange = (updates: Partial<CabDefinitionFormData>) => {
    if (!formData) return;
    setFormData({ ...formData, ...updates });
    const clearedKeys = Object.keys(updates) as Array<keyof CabDefinitionFormData>;
    setErrors((prev) => {
      const next = { ...prev };
      clearedKeys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const handleUpdate = async () => {
    if (!formData || !id) return;

    const validationErrors = validateCabDefinitionForm(formData);
    if (hasValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors before updating.');
      return;
    }

    try {
      const payload = formDataToUpdatePayload(formData);
      const updated = await updateMutation.mutateAsync(payload);
      if (!updated) {
        toast.error('CAB Definition not found.');
        return;
      }
      toast.success('CAB Definition updated successfully.');
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(message || 'Failed to update CAB Definition. Please try again.');
    }
  };

  const setActiveTab = (tab: DetailTab) => {
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-auto">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              CAB Meeting Management
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              All CAB Definitions
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium">{record?.name || id}</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4">
        {isLoading ? (
          <p className="text-gray-600">Loading...</p>
        ) : !record || !formData ? (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">CAB Definition</h1>
            <p className="text-gray-600 mb-6">Record not found.</p>
            <button
              type="button"
              onClick={() => navigate('/admin/cab-meeting-management/cab-definitions')}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to CAB Definitions
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
                type="button"
                onClick={handleUpdate}
                disabled={updateMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? 'Updating...' : 'Update'}
              </button>
            </div>

            <div className="mb-6 pb-4 border-b border-gray-200">
              <h1 className="text-xl font-bold text-gray-900">{record.name}</h1>
              <p className="text-sm text-gray-600 mt-1">CAB Definition</p>
            </div>

            <CabDefinitionForm
              formData={formData}
              errors={errors}
              onChange={handleChange}
              onSubmit={(_action) => { void handleUpdate(); }}
              onCancel={() => navigate('/admin/cab-meeting-management/cab-definitions')}
              isSaving={updateMutation.isPending}
              nameReadOnly
              showFooter={false}
            />
          </>
        )}
      </div>

      {record && id && (
        <div className="bg-white shadow rounded-lg flex flex-col min-h-[400px]">
          <div className="px-4 pt-4 pb-2 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-indigo-600">Related Links</h2>
          </div>

          <div className="border-b border-gray-200">
            <nav className="flex" aria-label="CAB Definition tabs">
              <button
                type="button"
                onClick={() => setActiveTab('schedule')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'schedule'
                    ? 'border-green-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Schedule Entries
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('meetings')}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'meetings'
                    ? 'border-green-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                CAB Meetings
              </button>
            </nav>
          </div>

          <div className="flex-1 min-h-0">
            {activeTab === 'schedule' ? (
              <ScheduleEntriesTab cabDefinitionId={id} />
            ) : (
              <CabMeetingsTab cabDefinitionId={id} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CabDefinitionDetail;
